/**
 * Settings → Devices: the phones signed in to MyDeck (mobile phase M1).
 *
 * A phone stays signed in for a year (the owner's choice), which is only
 * acceptable because any one of them can be signed out from here — from the
 * browser, or from another phone. Signing out takes effect on that phone's
 * next request. The browser's own session is not listed: it is a 14-day
 * cookie, and the sign-out button in the sidebar ends it.
 *
 * Sign-out asks once, inline — no dialog for a two-word question, and no
 * single tap that can lock a phone out by accident.
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Smartphone } from "lucide-react";
import * as React from "react";
import { toast } from "sonner";

import { useAuthStatus } from "@/components/AuthGate";
import { Badge, Button, EmptyState, ErrorState, Panel, Skeleton } from "@/components/ui/primitives";
import { devicesApi, type SignedInDevice } from "@/lib/api/client";
import { formatDate, formatRelativeTime } from "@/lib/format";

const KEY = ["auth", "devices"] as const;

export function DevicesPanel() {
  const { data: auth } = useAuthStatus();
  const enabled = Boolean(auth?.auth_enabled);

  return (
    <Panel className="overflow-hidden">
      <div className="p-5">
        <h2 className="font-display text-base font-medium text-ink">Devices</h2>
        <p className="mt-1 max-w-prose text-sm text-muted">
          Phones signed in to the MyDeck app. Each stays signed in for a year, so if one is
          lost or sold, sign it out here — it is locked out on its next request.
        </p>
      </div>
      {auth && !enabled ? (
        <p className="border-t border-hairline px-5 py-4 text-sm text-muted">
          Sign-in is off on this server, so phones don't need to sign in and none are listed.
          It is on in production.
        </p>
      ) : (
        enabled && <DeviceList />
      )}
    </Panel>
  );
}

function DeviceList() {
  const { data, isLoading, isError, error, refetch } = useQuery({
    queryKey: KEY,
    queryFn: devicesApi.list,
  });

  if (isLoading) {
    return (
      <div className="space-y-3 border-t border-hairline px-5 py-4" aria-busy>
        <Skeleton className="h-10 w-full" />
        <Skeleton className="h-10 w-2/3" />
      </div>
    );
  }
  if (isError) {
    return (
      <div className="border-t border-hairline">
        <ErrorState
          title="Couldn't load your devices"
          message={(error as Error).message}
          onRetry={() => void refetch()}
        />
      </div>
    );
  }
  if (!data?.length) {
    return (
      <div className="border-t border-hairline">
        <EmptyState
          icon={Smartphone}
          title="No phones signed in"
          body="Sign in on the MyDeck Android app and your phone appears here, ready to be signed out from anywhere."
        />
      </div>
    );
  }
  return (
    <ul className="divide-y divide-hairline border-t border-hairline">
      {data.map((d) => (
        <DeviceRow key={d.id} device={d} />
      ))}
    </ul>
  );
}

function DeviceRow({ device }: { device: SignedInDevice }) {
  const qc = useQueryClient();
  const [confirming, setConfirming] = React.useState(false);

  const signOut = useMutation({
    mutationFn: () => devicesApi.signOut(device.id),
    onSuccess: () => {
      toast.success(`${device.label} is signed out.`);
      void qc.invalidateQueries({ queryKey: KEY });
      // Signing out the phone in your hand ends this session too.
      if (device.current) void qc.invalidateQueries({ queryKey: ["auth", "status"] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  return (
    <li className="flex flex-wrap items-center gap-x-4 gap-y-2 px-5 py-3.5">
      <div className="flex min-w-0 flex-1 items-center gap-3">
        <div className="rounded-xl border border-hairline bg-raised p-2">
          <Smartphone className="h-4 w-4 text-muted" aria-hidden />
        </div>
        <div className="min-w-0">
          <p className="flex flex-wrap items-center gap-2 text-sm text-ink">
            <span className="truncate">{device.label}</span>
            {device.current && <Badge tone="neutral">This phone</Badge>}
          </p>
          <p className="mt-0.5 text-xs text-muted">
            Signed in {formatDate(device.signed_in_at)}
            {device.last_seen_at && <> · last used {formatRelativeTime(device.last_seen_at)}</>}
          </p>
        </div>
      </div>

      {confirming ? (
        // Its own line on a phone, so the question never squeezes the name.
        <div
          className="flex basis-full items-center justify-end gap-2 sm:basis-auto"
          role="group"
          aria-label={`Sign out ${device.label}?`}
        >
          <span className="text-xs text-muted">
            {device.current ? "Sign out this phone?" : "Sign it out?"}
          </span>
          <Button
            variant="danger"
            size="sm"
            onClick={() => signOut.mutate()}
            disabled={signOut.isPending}
          >
            {signOut.isPending ? "Signing out…" : "Sign out"}
          </Button>
          <Button variant="ghost" size="sm" onClick={() => setConfirming(false)}>
            Cancel
          </Button>
        </div>
      ) : (
        <Button variant="ghost" size="sm" onClick={() => setConfirming(true)}>
          Sign out
        </Button>
      )}
    </li>
  );
}
